'use client'

import React, { useEffect, useState } from 'react'
import { X, BookOpen, Minimize2, Maximize2, Expand, Shrink, Download } from 'lucide-react'
import { useDocumentationContext } from './documentation-provider'
import { DocumentationNav } from './documentation-nav'
import { DocumentationContent } from './documentation-content'
import { DocumentationSearch } from './documentation-search'
import { UntitledBadge } from '@/components/ui/untitled-badge'
import { useDocumentationContent } from '@/hooks/useDocumentationContent'
import { exportDocumentationToDocx } from '@/utils/documentation-export'
import { cn } from '@/lib/utils'

export function DocumentationPanel() {
  const { isOpen, close } = useDocumentationContext()
  const { content } = useDocumentationContent()
  const [mounted, setMounted] = useState(false)
  const [isMinimized, setIsMinimized] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [isExporting, setIsExporting] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])
  
  const handleExport = async () => {
    if (!content || isExporting) return
    
    setIsExporting(true)
    try {
      await exportDocumentationToDocx(content)
    } catch (error) {
      console.error('Failed to export documentation:', error)
    } finally {
      setIsExporting(false)
    }
  }

  if (!mounted || !isOpen) return null

  return (
    <>
      {/* Chatbox-style Panel (like Intercom) */}
      <div
        className={cn(
          "fixed z-50",
          "bg-white shadow-2xl",
          "transition-all duration-300 ease-in-out",
          isFullscreen ? [
            "inset-0",
            "w-full h-full",
            "rounded-none"
          ] : isMinimized ? [
            "bottom-6 right-6",
            "w-[480px] h-[60px]",
            "rounded-2xl"
          ] : [
            "bottom-6 right-6",
            "w-[480px] h-[650px]",
            "lg:w-[550px] lg:h-[700px]",
            "xl:w-[600px]",
            "rounded-2xl"
          ]
        )}
        style={!isFullscreen ? {
          maxHeight: isMinimized ? '60px' : 'calc(100vh - 48px)',
          maxWidth: 'calc(100vw - 48px)'
        } : undefined}
        aria-modal="true"
        role="dialog"
        aria-label="User Guide"
      >
        {/* Header */}
        <div className={cn(
          "bg-purple-600 text-white px-5 py-4",
          isFullscreen ? "" : "rounded-t-2xl",
          "flex items-center justify-between",
          "select-none",
          isMinimized && !isFullscreen && "rounded-b-2xl cursor-pointer"
        )}
        onClick={() => isMinimized && !isFullscreen && setIsMinimized(false)}
        >
          <div className="flex items-center gap-3">
            <BookOpen className="h-5 w-5" />
            <h2 className="text-base font-semibold">User Guide</h2>
            <UntitledBadge status="v1.2" variant="modern" className="bg-white/20 text-white border-white/30" />
          </div>
          <div className="flex items-center gap-2">
            {/* Export button - only show when not minimized */}
            {!isMinimized && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  handleExport()
                }}
                disabled={isExporting}
                className={cn(
                  "px-3 py-1.5 rounded-lg text-sm",
                  "hover:bg-white/10",
                  "transition-colors",
                  "flex items-center gap-2",
                  isExporting && "opacity-50 cursor-not-allowed"
                )}
                aria-label="Export Documentation"
              >
                <Download className="h-4 w-4" />
                <span className="hidden sm:inline">
                  {isExporting ? 'Exporting...' : 'Export Documentation'}
                </span>
              </button>
            )}
            
            {/* Fullscreen button - only show when not minimized */}
            {!isMinimized && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setIsFullscreen(!isFullscreen)
                }}
                className={cn(
                  "p-1.5 rounded-lg",
                  "hover:bg-white/10",
                  "transition-colors"
                )}
                aria-label={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
              >
                {isFullscreen ? (
                  <Shrink className="h-4 w-4" />
                ) : (
                  <Expand className="h-4 w-4" />
                )}
              </button>
            )}
            
            {/* Minimize button - only show when not in fullscreen */}
            {!isFullscreen && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setIsMinimized(!isMinimized)
                }}
                className={cn(
                  "p-1.5 rounded-lg",
                  "hover:bg-white/10",
                  "transition-colors"
                )}
                aria-label={isMinimized ? "Maximize" : "Minimize"}
              >
                {isMinimized ? (
                  <Maximize2 className="h-4 w-4" />
                ) : (
                  <Minimize2 className="h-4 w-4" />
                )}
              </button>
            )}
            
            {/* Close button */}
            <button
              onClick={(e) => {
                e.stopPropagation()
                if (isFullscreen) {
                  setIsFullscreen(false)
                }
                close()
              }}
              className={cn(
                "p-1.5 rounded-lg",
                "hover:bg-white/10",
                "transition-colors"
              )}
              aria-label="Close User Guide"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Body - Hidden when minimized */}
        {!isMinimized && (
          <>
            {/* Search */}
            <DocumentationSearch />

            {/* Content Area */}
            <div className={cn(
              "flex overflow-hidden",
              isFullscreen ? "h-[calc(100%-120px)]" : "h-[calc(100%-120px)] rounded-b-2xl"
            )}>
              {/* Left Navigation */}
              <DocumentationNav />
              
              {/* Right Content */}
              <DocumentationContent />
            </div>
          </>
        )}
      </div>
    </>
  )
}